import { Navigate } from 'react-router'

import { Card, CardContent } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ConfirmInfoStage, useConfirmInfoState } from '@/models/confirmInfo'
import { FillApi, FillApiContext, FillOpeningState } from '@/models/fillApplication'
import { useOrganisation } from '@/models/user'
import { ConfirmCarrierCard } from './ConfirmCarrierCard'
import { ConfirmInfoForm } from './ConfirmInfoForm'
import { ContractorConfirmForm } from './ContractorConfirmForm'
import { GoToQuotes } from './GoToQuotes'

/** Intake's parts for the customer: find the carrier first, then the review. */
export function ConfirmInfoView({ orgId }: { orgId: string }) {
  const { organisation } = useOrganisation(orgId)
  const { state, isLoading, isError } = useConfirmInfoState(orgId)

  if (!state || !organisation) {
    if (isLoading || !isError) return <SkeletonRows className="mt-10" rows={6} rowClassName="h-16" />
    return (
      <Card>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            We couldn&apos;t load your application. Refresh the page to try again.
          </p>
        </CardContent>
      </Card>
    )
  }
  if (state.stage === ConfirmInfoStage.SENT) return <GoToQuotes applicationId={state.application?.id} />
  // No longer shopping: the app's landing page knows where they belong.
  if (state.openingState === FillOpeningState.READ_ONLY) return <Navigate to="/insure" replace />

  return (
    <FillApiContext.Provider value={FillApi.CUSTOMER}>
      <div className="mx-auto flex w-full max-w-250 flex-col gap-6">
        <h1 className="text-ink text-3xl font-extrabold tracking-tight">Review your application</h1>
        {state.contractor ? (
          <ContractorConfirmForm
            orgId={orgId}
            organisation={organisation}
            state={state}
            contractor={state.contractor}
          />
        ) : state.openingState === FillOpeningState.FMCSA ? (
          <ConfirmCarrierCard orgId={orgId} />
        ) : (
          <ConfirmInfoForm orgId={orgId} organisation={organisation} state={state} />
        )}
      </div>
    </FillApiContext.Provider>
  )
}
